import React from 'react';
import { X, Calendar, Users, FileText, ClipboardList, Package2, DollarSign, BarChart3, Settings, Clock, MapPin, Car, Building2, UsersRound, Wrench, CreditCard, Truck, CheckSquare, MessageSquare, Phone, Mail, Zap, Shield, AlertTriangle, TrendingUp, PieChart, Archive } from 'lucide-react';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';

interface NavItem {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  pageId?: string; // For application settings check
  permissionKey?: string; // For permission check
}

interface HorizontalNavConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: NavItem) => void;
  slotIndex: number;
}

// Define navigation items in the exact same order as they appear in LeftNavigation.tsx
const availableNavItems: NavItem[] = [
  // Top navigation items (mobile section)
  { id: 'kpi-dashboard', name: 'KPI Dashboard', icon: PieChart, color: 'bg-gray-500', pageId: 'kpi-dashboard', permissionKey: 'kpiDashboard' },
  { id: 'calendar', name: 'Location Calendar', icon: Calendar, color: 'bg-blue-500', pageId: 'calendarV2', permissionKey: 'calendar' },
  { id: 'mobile-calendar', name: 'Mobile Calendar', icon: Car, color: 'bg-green-500', pageId: 'mobile-calendar', permissionKey: 'calendar' },
  { id: 'bookings', name: 'Bookings', icon: MapPin, color: 'bg-purple-500', pageId: 'bookings', permissionKey: 'bookings' },
  { id: 'proposals', name: 'Proposals', icon: FileText, color: 'bg-orange-500', pageId: 'proposals', permissionKey: 'proposals' },
  { id: 'customers', name: 'Customers', icon: Users, color: 'bg-indigo-500', pageId: 'customers', permissionKey: 'customers' },
  
  // Manage section
  // NOTE: Messages removed - now available as permanent standalone button
  { id: 'crm', name: 'CRM', icon: Building2, color: 'bg-blue-600', pageId: 'crm', permissionKey: 'crm' },
  { id: 'payroll', name: 'Payroll', icon: DollarSign, color: 'bg-teal-500', pageId: 'payroll', permissionKey: 'payroll' },
  { id: 'timeclock', name: 'Timeclock', icon: Clock, color: 'bg-amber-500', pageId: 'timeclock', permissionKey: 'timeclock' },
  { id: 'supplies', name: 'Supplies', icon: Package2, color: 'bg-yellow-500', pageId: 'supplies', permissionKey: 'supplies' },
  { id: 'services', name: 'Services', icon: Wrench, color: 'bg-pink-500', pageId: 'services', permissionKey: 'services' },
  { id: 'resources', name: 'Resources', icon: UsersRound, color: 'bg-cyan-500', pageId: 'resources', permissionKey: 'resources' },
  { id: 'automations', name: 'Automations', icon: Zap, color: 'bg-fuchsia-500', pageId: 'automations', permissionKey: 'automations' },
  { id: 'automations-v2', name: 'Automations v2', icon: Zap, color: 'bg-purple-600', pageId: 'automations', permissionKey: 'automations' },
  { id: 'tasks', name: 'Tasks', icon: CheckSquare, color: 'bg-red-500', pageId: 'tasks', permissionKey: 'tasks' },
  
  // Reports section
  { id: 'sales-reports', name: 'Sales Reports', icon: TrendingUp, color: 'bg-sky-500', pageId: 'sales-reports', permissionKey: 'salesReports' },
  { id: 'team-reports', name: 'Team Reports', icon: BarChart3, color: 'bg-stone-500', pageId: 'team-reports', permissionKey: 'teamReports' },
  { id: 'supplies-usage', name: 'Supplies Usage', icon: Archive, color: 'bg-zinc-500', pageId: 'supplies-usage', permissionKey: 'suppliesUsage' },
  
  // Settings (last)
  { id: 'settings', name: 'Settings', icon: Settings, color: 'bg-slate-600', pageId: 'settings', permissionKey: 'settings' }
];

export default function HorizontalNavConfigModal({ isOpen, onClose, onSelect, slotIndex }: HorizontalNavConfigModalProps) {
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();

  if (!isOpen) return null;

  // Filter navigation items based on permissions and application settings
  const filteredNavItems = availableNavItems.filter(item => {
    // Check if page is disabled in application settings
    if (item.pageId && isPageDisabled(item.pageId as any)) {
      return false;
    }

    // Check if user has permission to access this page
    if (item.permissionKey) {
      // Handle different permission keys based on what's actually available in canAccess
      switch (item.permissionKey) {
        case 'kpiDashboard':
          return canAccess.kpiDashboard;
        case 'calendar':
          return canAccess.calendar;
        case 'bookings':
          return canAccess.bookings;
        case 'customers':
          return canAccess.customers;
        case 'proposals':
          return canAccess.proposals;
        case 'messages':
          return canAccess.messages;
        case 'crm':
          return canAccess.crm;
        case 'tasks':
          return canAccess.tasks;
        case 'resources':
          return canAccess.resources;
        case 'supplies':
          return canAccess.supplies;
        case 'services':
          return canAccess.services;
        case 'payroll':
          return canAccess.payroll;
        case 'timeclock':
          return canAccess.timeclock;
        case 'automations':
          return canAccess.automations;
        case 'salesReports':
          return canAccess.salesReports;
        case 'teamReports':
          return canAccess.teamReports;
        case 'suppliesUsage':
          return canAccess.suppliesUsage;
        case 'settings':
          return canAccess.settings;
        default:
          return true; // Default to allowing access if permission key is unknown
      }
    }

    return true; // Allow items without specific permission requirements
  });

  const handleItemClick = (item: NavItem) => {
    onSelect(item);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Choose Navigation Item
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Select a page to add to position {slotIndex + 1}
          </p>
        </div>

        {/* App-like grid of navigation items */}
        <div className="grid grid-cols-3 gap-4">
          {filteredNavItems.length > 0 ? (
            filteredNavItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mb-2 group-hover:scale-105 transition-transform`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-xs text-gray-700 text-center leading-tight">
                    {item.name}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="col-span-3 text-center py-8">
              <p className="text-gray-500 text-sm">
                No navigation items available based on your permissions and settings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 